/* eslint-disable max-lines */

import { Provider, Theme } from '@dnb/eufemia/shared';
import { ThemeProvider } from '@emotion/react';
import { ErrorContextProvider } from '@portals/shared-frontend/components/ErrorContextProvider';
import React, { type JSX, lazy, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';

import AuthContextProvider from '@/auth/AuthContextProvider';
import InternalRoutes from '@/auth/InternalRoutes';
import SsoAzureLogin from '@/auth/SsoAzureLogin';
import CenteredProgressIndicator from '@/components/ProgressIndicator';
import CommonRepoApiDocumentation from '@/pages/api-documentation/CommonRepoApiDocumentation';
import DebugDuckyPage from '@/pages/debug-ducky';
import EventPage from '@/pages/events/[slug]/EventPage';
import ApiFromCommonRepo from '@/pages/explorer/apis/ApiFromCommonRepo';
import EventExplorerPage from '@/pages/explorer/events/EventExplorerPage';
import GettingStarted from '@/pages/getting-started';
import LoginFailure from '@/pages/login/LoginFailure';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ApiProviderPage } from '@/pages/profile/apps/application/ApiProviderPage';
import { VisibilityAndAccessManagementTab } from '@/pages/profile/apps/application/ApiProviderPage/VisibilityAndAccessManagementTab';
import AsyncApiModal from '@/pages/profile/apps/application/asyncapi';
import Sitemap from '@/pages/sitemap/Sitemap';
import Status from '@/pages/status/Status';
import ManageSubscriptions from '@/pages/subscriptions/ManageSubscriptions';
import NewsSubscriptions from '@/pages/subscriptions/NewsSubscriptions';
import ReleasesSubscriptions from '@/pages/subscriptions/ReleasesSubscriptions';
import StatusSubscriptions from '@/pages/subscriptions/StatusSubscriptions';
import Unsubscribe from '@/pages/subscriptions/Unsubscribe';
import { SWRConfigProvider } from '@/provider/SWRConfigProvider';
import { allIsAwesome } from '@/theme/awesome';
import { darkTheme } from '@/theme/dark';
import { defaultTheme, ThemeMapping } from '@/theme/default';

import App from './App';
import SuccessModal from './components/SuccessModal';
import AggregationProviders from './pages/aggregation-providers/AggregationProviders';
import ApiTermsOfUse from './pages/api-terms-of-use/ApiTermsOfUse';
import ApiExplorer from './pages/explorer/apis/page/api-explorer';
import CommercialApis from './pages/explorer/apis/page/commercial-apis';
import InternalApis from './pages/explorer/apis/page/internal-apis';
import RegulatoryApis from './pages/explorer/apis/page/regulatory-apis';
import ServiceExplorer from './pages/explorer/apis/page/service-explorer';
import AddMcp from './pages/explorer/mcps/AddMcp';
import EditMcpGuide from './pages/explorer/mcps/EditMcpGuide';
import McpDocumentation from './pages/explorer/mcps/McpDocumentation';
import McpExplorerPage from './pages/explorer/mcps/McpExplorerPage';
import GettingStartedAPIConsumers from './pages/getting-started/api-consumers';
import GettingStartedApiProviders from './pages/getting-started/api-providers';
import OAuth2AuthenticationGuide from './pages/getting-started/Oauth2AuthenticationGuide';
import Jobs from './pages/jobs/Jobs';
import Landing from './pages/landing';
import ForgotPassword from './pages/login/ForgotPassword';
import Login from './pages/login/Login';
import PrivacyPolicy from './pages/privacy-policy/PrivacyPolicy';
import { ConsumersTab } from './pages/profile/apps/application/ApiProviderPage/ConsumersTab';
import { LifecycleManagementTab } from './pages/profile/apps/application/ApiProviderPage/LifecycleManagementTab';
import { OverviewTab } from './pages/profile/apps/application/ApiProviderPage/OverviewTab';
import Application from './pages/profile/apps/application/Application';
import UpdateClientForm from './pages/profile/apps/application/components/Client/components/UpdateClient';
import Entitlements from './pages/profile/apps/Entitlements';
import NewPage from './pages/profile/teams/team-pages/NewPage';
import SignUp from './pages/sign-up/SignUp';
import SignUpSuccess from './pages/sign-up/SignUpSucces';
import TermsOfUse from './pages/terms-of-use/TermsOfUse';
import { AwsRumProvider } from './rum';

import './index.css';

const ChannelStatistics = lazy(() => import('@/pages/channel-statistics/ChannelStatistics'));
const ImportClients = lazy(() => import('@/pages/ImportClients/index'));
const ApiDocumentation = lazy(() => import('@/pages/api-documentation/ApiDocumentation'));
const News = lazy(() => import('./pages/news/News'));
const Search = lazy(() => import('./pages/search'));
const ContentPage = lazy(() => import('./pages/content/ContentPage'));
const HelpCenter = lazy(() => import('./pages/help-center/HelpCenter'));
const SupportFlow = lazy(() => import('./pages/help-center/SupportFlow'));
const Faq = lazy(() => import('@/pages/help-center/faq/Faq'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const OAuthIntegrationInfoRequester = lazy(() => import('./pages/OAuthIntegrationInfoRequesterPage'));

const CreateTeam = lazy(() => import('./pages/profile/teams/CreateTeam'));
const GeneratePsd2Certificate = lazy(() => import('./pages/profile/psd2/GeneratePsd2Certificate'));
const EnableMfa = lazy(() => import('./pages/mfa/EnableMfa'));
const CreateApp = lazy(() => import('./pages/profile/apps/createApp'));
const CreateConsumerApp = lazy(() => import('./pages/profile/apps/CreateConsumerApp'));
const CreateClient = lazy(() => import('./pages/profile/apps/application/components/Client/components/CreateClient'));

const ImportClient = lazy(() => import('./pages/profile/apps/application/components/ImportClient'));

const EditApp = lazy(() => import('./pages/profile/apps/editApp'));

const EditProject = lazy(() => import('./pages/profile/apps/editProject'));
const AddTeamMembers = lazy(() => import('./pages/profile/teams/AddTeamMembers'));
const TeamPages = lazy(() => import('./pages/profile/teams/team-pages/TeamPages'));

const EditTeam = lazy(() => import('./pages/profile/teams/EditTeam'));
const Team = lazy(() => import('./pages/profile/teams/Team'));
const DelegatedAuthentication = lazy(() => import('./pages/delegated-authentication/DelegatedAuthentication'));
const ScopeOverview = lazy(() => import('./pages/scope-overview'));
const TeamPagesOverview = lazy(() => import('./pages/team-pages-overview'));
const JwtValidator = lazy(() => import('./pages/jwt/JwtValidator'));

const RUM_DATA_ENDPOINT = 'https://dataplane.rum.eu-west-1.amazonaws.com';
const APPLICATION_REGION = 'eu-west-1';

let localTheme = {};
let localThemeName = null;
if (localStorage.getItem('theme') && localStorage.getItem('theme')) {
  localTheme = localStorage.getItem('theme') === 'awesome' ? allIsAwesome : darkTheme;
  localThemeName = localStorage.getItem('theme');
}

const Redirect = ({ to }: { to: string }) => {
  const navigate = useNavigate();
  useEffect(() => navigate(to, { replace: true }), [navigate, to]);
  return null;
};

function Site(): JSX.Element {
  useEffect(() => {
    globalThis.version = { version: `${APP_VERSION}.${GIT_HASH}` };
  }, []);

  // Include Adobe script if present in env variables
  useEffect(() => {
    if (!ADOBE_LAUNCH_URL) return;

    const script = document.createElement('script');
    script.src = ADOBE_LAUNCH_URL;
    script.async = true;
    document.head.append(script);
  }, []);

  const appHome = (
    <>
      <Route path="application/:id">
        <Route element={<Application />} index />
        <Route element={<EditApp />} path="edit" />
        <Route element={<AsyncApiModal />} path="asyncapi" />
      </Route>
      <Route element={<CreateApp />} path="application/new" />
      <Route element={<CreateClient />} path="application/:appId/create-client" />

      <Route element={<ImportClient />} path="application/:appId/import-client" />
      <Route element={<ApiProviderPage />} path="application/:appId/api/:apiId">
        <Route element={<OverviewTab />} index />
        <Route element={<LifecycleManagementTab />} path="lifecycle-management" />
        <Route element={<VisibilityAndAccessManagementTab />} path="visibility-access" />
        <Route element={<ConsumersTab />} path="consumers" />
      </Route>
    </>
  );

  const projectRoute = (
    <Route path="project/:id">
      <Route element={<EditProject />} path="edit" />
    </Route>
  );

  return (
    <AwsRumProvider
      allowCookies
      enableXRay={false}
      endpoint={RUM_DATA_ENDPOINT}
      guestRoleArn={RUM_GUEST_ROLE_ARN}
      id={RUM_APPLICATION_ID}
      identityPoolId={RUM_IDENTITY_POOL_ID}
      region={APPLICATION_REGION}
      sessionSampleRate={1}
      telemetries={['http', 'errors', 'performance']}
      version={APP_VERSION}
    >
      <AuthContextProvider>
        <ErrorContextProvider>
          <SWRConfigProvider>
            <BrowserRouter>
              <Suspense fallback={<CenteredProgressIndicator />}>
                <Routes>
                  <Route element={<App />} path="/">
                    <Route element={<Landing />} index />
                    <Route element={<SuccessModal />} path="success" />
                    <Route element={<ApiTermsOfUse />} path="documentation/:apiSlug/tou" />
                    <Route element={<Jobs />} path="jobs" />

                    <Route element={<HelpCenter />} path="help-center" />
                    <Route element={<SupportFlow />} path="help-center/:id" />
                    <Route path="help-center/faq">
                      <Route element={<Redirect to="psd2" />} index />
                      <Route element={<Faq />} path=":tab" />
                    </Route>
                    <Route element={<Redirect to="/help-center" />} path="support" />

                    <Route element={<TermsOfUse />} path="terms-of-use" />
                    <Route element={<PrivacyPolicy />} path="privacy-policy" />
                    <Route element={<News />} path="news" />
                    <Route element={<Search />} path="search" />
                    <Route element={<ContentPage />} path="content/*" />
                    <Route element={<Status />} path="status" />
                    <Route element={<Sitemap />} path="site-map" />

                    <Route element={<ChannelStatistics />} path="channel-statistics" />
                    <Route element={<SsoAzureLogin />} path="/sso" />
                    <Route path="login">
                      <Route element={<Login />} index />
                      <Route element={<LoginFailure />} path="failure" />
                      <Route element={<ForgotPassword />} path="forgotten-password" />
                    </Route>
                    <Route path="sign-up">
                      <Route element={<SignUp />} index />
                      <Route element={<SignUpSuccess />} path="success" />
                    </Route>

                    <Route element={<EnableMfa />} path="mfa/enable" />

                    <Route element={<DelegatedAuthentication />} path="delegated-authentication" />

                    <Route element={<InternalRoutes />} path="aggregation">
                      <Route element={<AggregationProviders />} index />
                    </Route>

                    {appHome}
                    {projectRoute}
                    <Route element={<InternalRoutes />} path="integrate-servicenow">
                      <Route element={<OAuthIntegrationInfoRequester />} index />
                    </Route>

                    <Route element={<InternalRoutes />} path="import-clients">
                      <Route element={<ImportClients />} index />
                    </Route>

                    <Route path="profile">
                      <Route element={<Redirect to="teams" />} index />
                      <Route path="apps">
                        <Route element={<Profile tab="apps" />} index />
                      </Route>
                      <Route path="teams">
                        <Route element={<Profile tab="teams" />} index />
                        <Route element={<CreateTeam />} path="new" />
                      </Route>
                      <Route path="psd2">
                        <Route element={<Profile tab="psd2" />} index />
                        <Route element={<GeneratePsd2Certificate />} path="generate-certificate" />
                      </Route>
                      <Route path="account">
                        <Route element={<Profile tab="account" />} index />
                        <Route element={<EnableMfa />} path="mfa/enable" />
                      </Route>
                    </Route>

                    <Route path="explorer">
                      <Route element={<ServiceExplorer />} index />
                      <Route element={<InternalRoutes />} path="events">
                        <Route element={<EventExplorerPage />} index />
                      </Route>
                      <Route element={<ApiExplorer />} path="apis" />
                      <Route element={<InternalRoutes />} path="apis/internal">
                        <Route element={<InternalApis />} index />
                      </Route>
                      <Route element={<CommercialApis />} path="apis/commercial" />
                      <Route element={<RegulatoryApis />} path="apis/regulatory" />
                      <Route element={<InternalRoutes />} path="mcps">
                        <Route element={<McpExplorerPage />} index />
                        <Route element={<AddMcp />} path="add" />
                        <Route element={<McpDocumentation />} path=":slug" />
                      </Route>
                    </Route>
                    <Route element={<InternalRoutes />} path="mcp-servers">
                      <Route element={<McpDocumentation />} path=":api" />
                      <Route element={<McpDocumentation />} path=":api/:environment/:version" />
                      <Route element={<McpDocumentation />} path=":api/:environment/:version/guide" />
                      <Route element={<EditMcpGuide />} path=":api/:environment/:version/guide/edit" />
                    </Route>
                    <Route element={<InternalRoutes />} path="events">
                      <Route element={<EventPage />} path=":api/:environment/:version/:tab" />
                    </Route>
                    <Route element={<InternalRoutes />} path="api-contracts">
                      <Route element={<ApiFromCommonRepo />} index />
                      <Route element={<CommonRepoApiDocumentation />} path=":apiSlug/:environment/:version" />
                      <Route element={<CommonRepoApiDocumentation />} path=":apiSlug/:environment/:version/:tab" />
                    </Route>
                    <Route caseSensitive path="documentation">
                      <Route element={<ApiDocumentation />} path=":api/:environment/:version" />
                      <Route element={<ApiDocumentation />} path=":api/:environment/:version/:tab" />
                    </Route>

                    <Route element={<ManageSubscriptions />} path="subscriptions/manage/:id" />

                    <Route element={<ManageSubscriptions />} path="subscriptions/manage" />

                    <Route path="team/:teamId">
                      <Route element={<Team />} index />
                      <Route element={<TeamPages />} path="pages/:contentId" />
                      <Route element={<NewPage />} path="pages/new" />
                      <Route element={<CreateApp />} path="application/new" />
                      <Route element={<CreateConsumerApp />} path="application/consumer/new" />
                      <Route element={<EditTeam />} path="edit-team" />
                      <Route element={<Entitlements />} path="application/:appId/attach-api" />
                      <Route element={<Entitlements />} path="application/:appId/edit-api/:apiId" />
                      <Route element={<CreateClient />} path="application/:appId/create-client" />
                      <Route element={<UpdateClientForm />} path="application/:appId/update-client/:clientId" />

                      <Route element={<AddTeamMembers isNewTeam />} path="invite-users" />
                      <>
                        <Route element={<CreateApp />} path="application/new" />
                        {appHome}
                        {projectRoute}
                      </>
                    </Route>

                    <Route path="getting-started">
                      <Route element={<GettingStarted />} index />
                      <Route element={<GettingStartedApiProviders />} path="api-providers" />
                      <Route element={<GettingStartedAPIConsumers />} path="dnb-internal-consumers" />
                      <Route element={<OAuth2AuthenticationGuide />} path="oauth2-authentication" />
                    </Route>

                    <Route element={<SsoAzureLogin />} path="login-complete/azure" />
                    <Route element={<ReleasesSubscriptions />} path="/subscriptions/releases/:apiSlug" />
                    <Route element={<NewsSubscriptions />} path="/subscriptions/news" />
                    <Route element={<StatusSubscriptions />} path="/subscriptions/status" />
                    <Route element={<Unsubscribe />} path="/unsubscribe" />
                    <Route element={<ScopeOverview />} path="scope-overview" />
                    <Route element={<TeamPagesOverview />} path="pages" />
                    <Route element={<Redirect to="/pages" />} path="team-pages" />
                    <Route element={<JwtValidator />} path="/jwt-validator" />
                    <Route element={<DebugDuckyPage />} path="/debug-ducky" />
                    <Route element={<NotFoundPage />} path="*" />
                  </Route>
                </Routes>
              </Suspense>
            </BrowserRouter>
          </SWRConfigProvider>
        </ErrorContextProvider>
      </AuthContextProvider>
    </AwsRumProvider>
  );
}

const root = createRoot(document.getElementById('app')!);

root.render(
  <React.StrictMode>
    <ThemeMapping>
      <Theme propMapping={`theme-${localThemeName}`}>
        <ThemeProvider theme={defaultTheme}>
          <ThemeProvider theme={localTheme}>
            <Provider locale="en-GB">
              <Site />
            </Provider>
          </ThemeProvider>
        </ThemeProvider>
      </Theme>
    </ThemeMapping>
  </React.StrictMode>,
);
